import type { CSSProperties } from "react";

/**
 * Scuttle — Grafty's Chief Morale Officer.
 *
 * Hand-traced from the approved reference graphic of the real Scuttle: an
 * outlined cartoon (cel) style with spiky fur — big upright ears with deep
 * inner-ear, a messy fringe tuft, warm round eyes, an open happy mouth with
 * a pink tongue, and a blue collar with a hanging silver tag.
 *
 * Moods only change the face — the dog stays the same dog:
 *  - default / success / cute: the open tongue-out grin (cute adds a blush)
 *  - empty:                    a content closed smile
 *  - error:                    worried brows + a small frown
 *
 * Flat fills + strokes only (no gradients/clipPaths), so multiple instances
 * per page never collide and it renders crisply from 34px to 300px. The
 * caption uses `currentColor`, so it follows the surrounding text color in
 * both light and dark themes.
 */

export type ScuttleMood = "default" | "error" | "empty" | "success" | "cute";

interface ScuttleProps {
  mood?: ScuttleMood;
  size?: number;
  subtitle?: string;
  showSubtitle?: boolean;
  className?: string;
  style?: CSSProperties;
}

const CAPTION: Record<ScuttleMood, string> = {
  default: "Chief Morale Officer",
  error: "Something went wrong",
  empty: "Nothing here yet",
  success: "All done!",
  cute: "Welcome to Grafty!",
};

// Palette from the approved reference graphic
const OUT = "#4A2B15"; // dark outline
const FUR = "#D5A15E"; // main tan
const FURLT = "#EAC98F"; // light fur (muzzle, brows, chest)
const INNER = "#B5854D"; // inner ear
const NOSE = "#2E1B0E";
const TONGUE = "#F0879B";
const TONGUE_LINE = "#C2647A";
const MOUTH = "#5E2C24";
const COLLAR = "#3F6BB5";
const COLLAR_DARK = "#2A4A85";
const TAG = "#C9D6E2";
const BLUSH = "#E8A177";

export function Scuttle({
  mood = "default",
  size = 160,
  subtitle,
  showSubtitle = true,
  className,
  style,
}: ScuttleProps) {
  const label = subtitle ?? CAPTION[mood];
  const happy = mood !== "error" && mood !== "empty";
  const vbH = showSubtitle ? 230 : 200;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      viewBox={`0 0 200 ${vbH}`}
      role="img"
      aria-label={`Scuttle the dog — ${label}`}
      className={className}
      style={style}
    >
      <title>{`Scuttle — ${label}`}</title>

      {/* ══ Ears — big, upright, slightly splayed; deep inner ear ══ */}
      <path
        d="M58 99 C44 84 30 50 30 26 C30 16 38 12.5 46 18 C68 35 85 59 92 79 L68 100 Z"
        fill={FUR} stroke={OUT} strokeWidth="4" strokeLinejoin="round"
      />
      <path d="M58 88 C48 74 40 54 39 34 C54 50 70 66 79 79 L64 90 Z" fill={INNER} />
      <path
        d="M142 99 C156 84 170 50 170 26 C170 16 162 12.5 154 18 C132 35 115 59 108 79 L132 100 Z"
        fill={FUR} stroke={OUT} strokeWidth="4" strokeLinejoin="round"
      />
      <path d="M142 88 C152 74 160 54 161 34 C146 50 130 66 121 79 L136 90 Z" fill={INNER} />

      {/* ══ Head — rounded with spiky cheek fur ══ */}
      <path
        d="M66 66
          C74 57 86 53 100 53
          C114 53 126 57 134 66
          C141 73 145 82 146 92
          L154 99 L146 105 L153 113 L143 117 L149 128 L139 129
          C136 142 128 151 117 156
          C106 160 94 160 83 156
          C72 151 64 142 61 129
          L51 128 L57 117 L47 113 L54 105 L46 99 L54 92
          C55 82 59 73 66 66 Z"
        fill={FUR} stroke={OUT} strokeWidth="4" strokeLinejoin="round"
      />

      {/* ══ Messy fringe tuft ══ */}
      <path
        d="M70 66 L73 52 L80 58 L83 42 L92 52 L98 33 L104 50 L110 44 L114 55 L124 47 L126 60 L133 66
          C122 56 111 53 100 53 C89 53 79 58 70 66 Z"
        fill={FUR} stroke={OUT} strokeWidth="3.5" strokeLinejoin="round"
      />

      {/* ══ Brow patches ══ */}
      <path d="M66 88 C68 81 74 77 82 78 C88 79 90 84 88 88 C82 86 73 86 66 88 Z" fill={FURLT} />
      <path d="M134 88 C132 81 126 77 118 78 C112 79 110 84 112 88 C118 86 127 86 134 88 Z" fill={FURLT} />
      {mood === "cute" && (
        <>
          <ellipse cx="63" cy="112" rx="7" ry="4" fill={BLUSH} opacity="0.55" />
          <ellipse cx="137" cy="112" rx="7" ry="4" fill={BLUSH} opacity="0.55" />
        </>
      )}
      {mood === "error" && (
        <>
          <path d="M67 89 L83 83" stroke={OUT} strokeWidth="3" strokeLinecap="round" />
          <path d="M133 89 L117 83" stroke={OUT} strokeWidth="3" strokeLinecap="round" />
        </>
      )}

      {/* ══ Eyes — warm brown iris, big pupil, highlights ══ */}
      <circle cx="79" cy="96" r="9" fill="#3A2412" />
      <circle cx="121" cy="96" r="9" fill="#3A2412" />
      <circle cx="79" cy="96.5" r="5.6" fill="#150C06" />
      <circle cx="121" cy="96.5" r="5.6" fill="#150C06" />
      <circle cx="75.5" cy="92.5" r="3" fill="#fff" />
      <circle cx="117.5" cy="92.5" r="3" fill="#fff" />
      <circle cx="82.5" cy="99.5" r="1.3" fill="#fff" opacity="0.5" />
      <circle cx="124.5" cy="99.5" r="1.3" fill="#fff" opacity="0.5" />

      {/* ══ Muzzle / beard — light, spiky lower edge ══ */}
      <path
        d="M69 111
          C75 104 87 100 100 100
          C113 100 125 104 131 111
          C135 120 134 130 129 138
          L122 143 L118 152 L110 146 L100 154 L90 146 L82 152 L78 143 L71 138
          C66 130 65 120 69 111 Z"
        fill={FURLT} stroke={OUT} strokeWidth="3.5" strokeLinejoin="round"
      />

      {/* ══ Nose ══ */}
      <path
        d="M89 112 C89 107 94 104.5 100 104.5 C106 104.5 111 107 111 112 C111 118 106 122 100 122 C94 122 89 118 89 112 Z"
        fill={NOSE} stroke={OUT} strokeWidth="2.5" strokeLinejoin="round"
      />
      <ellipse cx="95.5" cy="110" rx="2.6" ry="1.5" fill="#fff" opacity="0.35" />
      <path d="M100 122 L100 127" stroke={OUT} strokeWidth="2.5" strokeLinecap="round" />

      {/* ══ Mouth by mood ══ */}
      {happy && (
        <>
          <path
            d="M83 127 Q100 139 117 127 Q115 147 100 149 Q85 147 83 127 Z"
            fill={MOUTH} stroke={OUT} strokeWidth="3" strokeLinejoin="round"
          />
          <path
            d="M91 137 Q100 133.5 109 137 Q109 147 100 148.5 Q91 147 91 137 Z"
            fill={TONGUE} stroke={TONGUE_LINE} strokeWidth="1.6" strokeLinejoin="round"
          />
          <path d="M100 138 L100 146" stroke={TONGUE_LINE} strokeWidth="1.4" strokeLinecap="round" />
          <path
            d="M80 126 Q82 129 86 130 M120 126 Q118 129 114 130"
            stroke={OUT} strokeWidth="2.6" strokeLinecap="round" fill="none"
          />
        </>
      )}
      {mood === "empty" && (
        <path d="M85 128 Q100 137 115 128" stroke={OUT} strokeWidth="2.8" strokeLinecap="round" fill="none" />
      )}
      {mood === "error" && (
        <path d="M88 134 Q100 128 112 134" stroke={OUT} strokeWidth="2.8" strokeLinecap="round" fill="none" />
      )}

      {/* ══ Body / chest ruff ══ */}
      <path
        d="M50 200 C53 176 62 162 76 154 L100 150 L124 154 C138 162 147 176 150 200 Z"
        fill={FUR} stroke={OUT} strokeWidth="4" strokeLinejoin="round"
      />
      <path
        d="M70 200 C72 182 79 170 90 165 L88 173 L98 169 L97 177 L106 172 L107 180 L115 176 C123 182 127 190 128 200 Z"
        fill={FURLT} stroke={OUT} strokeWidth="3" strokeLinejoin="round"
      />

      {/* ══ Collar + hanging tag ══ */}
      <path
        d="M64 160 C82 149 118 149 136 160 L133 173 C116 162 84 162 67 173 Z"
        fill={COLLAR} stroke={COLLAR_DARK} strokeWidth="3.5" strokeLinejoin="round"
      />
      <circle cx="100" cy="171" r="3" fill={TAG} stroke={OUT} strokeWidth="2" />
      <circle cx="100" cy="181" r="8.5" fill={TAG} stroke={OUT} strokeWidth="2.8" />

      {showSubtitle && (
        <text
          x="100"
          y="218"
          textAnchor="middle"
          fill="currentColor"
          opacity="0.72"
          fontFamily='"Syne", system-ui, sans-serif'
          fontSize="13"
          fontWeight="700"
        >
          {label}
        </text>
      )}
    </svg>
  );
}

export default Scuttle;
